import React, {Fragment} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState, useLogout} from 'app/current-user/api';
import {useAccountSettings} from 'app/settings/api';
import {ACCOUNT_SWITCH_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import type {AuthenticatedUserSerializedType} from 'sweet-types';

type Props = {renderRegularLink?: boolean};

export function UserMenu({renderRegularLink = false}: Props) {
  const [requestLogout] = useLogout();

  const accountName = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: 900,
  }).data!.account_name;
  const currentUser = useCurrentUserState().data!;

  return (
    <UserMenuButton userData={currentUser}>
      <DropdownMenu>
        {currentUser.is_staff ? (
          <DropdownItem key="1" href="/a/sp-admin">
            <i className="far fa-fw fa-cogs" />
            <span>{gt('Admin')}</span>
          </DropdownItem>
        ) : null}
        <DropdownItem
          key="3"
          tag={FullHTMLLink}
          html_url="/accounts/overview/"
          renderRegularLink={renderRegularLink}
        >
          <i className="fa fa-fw fa-user-circle" />
          <span>{gt('My Account')}</span>
        </DropdownItem>
        {currentUser.num_accounts > 1 ? (
          <Fragment>
            <DropdownItem divider />
            <DropdownItem
              tag={FullHTMLLink}
              key="2"
              html_url={ACCOUNT_SWITCH_URL}
              renderRegularLink={renderRegularLink}
            >
              <div className="d-flex">
                <div>
                  <i className="fa fa-fw fa-external-link mt-1" />
                </div>
                <div>
                  {gt('Switch Account')}
                  <div className="curr-account small text-muted font-weight-bold text-truncate">
                    {accountName}
                  </div>
                </div>
              </div>
            </DropdownItem>
          </Fragment>
        ) : null}
        <DropdownItem divider />
        <DropdownItem key="4" onClick={() => requestLogout()}>
          <i className="fa fa-fw fa-sign-out" />
          <span>{gt('Logout')}</span>
        </DropdownItem>
      </DropdownMenu>
    </UserMenuButton>
  );
}

export interface UserMenuButtonProps {
  userData: AuthenticatedUserSerializedType;
  children: React.ReactNode;
  className?: string;
}

export const UserMenuButton = ({
  userData,
  children,
  className = 'd-none d-lg-flex',
}: UserMenuButtonProps): JSX.Element => (
  <StyledUserMenuContainerDiv className={className}>
    <DropdownToggle
      className="menu-button nav-link d-flex align-items-center"
      tag="a"
    >
      <UserAvatar user={userData} />
      <span className="text-truncate name ml-2 d-none d-xl-block">
        {userData.name}
      </span>
      <i className="ml-3 fa fa-angle-down" />
    </DropdownToggle>
    {children}
  </StyledUserMenuContainerDiv>
);

const StyledUserMenuContainerDiv = styled(UncontrolledDropdown)`
  align-self: stretch;
  background: ${colors.darkGreen};
  align-items: stretch;

  .menu-button {
    padding: 0 1.5rem !important;
    color: ${colors.blueGray};

    &,
    > * {
      transition: 250ms;
    }

    > .name {
      max-width: 200px;
    }

    > span,
    > i {
      color: ${colors.blueGray};
    }

    &:hover {
      cursor: pointer;
      > i,
      > span {
        color: #fff;
      }

      .avatar {
        border-color: #fff;
      }
    }

    .avatar {
      border: 2px solid ${colors.blueGray};
    }
  }

  .dropdown-item i {
    margin-right: 0.5rem;
  }
  .curr-account {
    max-width: 230px;
  }
`;
